import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { graphql } from '~/apollo/generated/v4';
import type { StudyIndexPageQuery } from '~/apollo/generated/v4/graphql';
import { Heading } from '~/components/common/Heading';
import { HtmlSection } from '~/components/common/HtmlSection';
import { LastUpdated } from '~/components/common/LastUpdated';
import { NotFound } from '~/components/common/NotFound';
import { Prose } from '~/components/common/Prose';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { StudyGeoreferencesMap } from '~/components/study/study-georeferences-map';
import { StudyLiteraturePanel } from '~/components/study/StudyLiteraturePanel';
import { StudyOutcropList } from '~/components/study/study-outcrop-list';
import { useRouteParam } from '~/hooks/routing';
import { cn } from '~/utils/common';
import { ApolloProviderV4 } from '~/apollo/client-v4';

const STUDY_INDEX_PAGE = graphql(`
  query StudyIndexPage($id: ID!) {
    studyGet(id: $id) {
      ...studyParts
      updatedAt
      outcrops {
        id
        name
        outcropCategory
        virtualOutcropModels {
          ...vomParts
        }
        georeferences {
          ...georeferenceParts
        }
        region {
          ...regionParts
        }
      }
      literatureReferences {
        ...literatureParts
        files {
          ...fileParts
        }
      }
      georeferences {
        ...georeferenceParts
      }
      crossSections {
        id
        name
        georeferences {
          ...georeferenceParts
        }
      }
      sedimentaryLogs {
        id
        name
        georeferences {
          ...georeferenceParts
        }
      }
      gigaPans {
        id
        name
        georeferences {
          ...georeferenceParts
        }
      }
    }
  }
`);

type Study = NonNullable<StudyIndexPageQuery['studyGet']>;
type Outcrop = Study['outcrops'][number];

function outcropCategoryLabel(outcrops: Outcrop[]) {
  const hasOutcropType = outcrops.find(oc => oc.outcropCategory === 'outcrop');
  const hasAnalogueType = outcrops.find(oc => oc.outcropCategory !== 'outcrop');

  if (hasOutcropType && hasAnalogueType) {
    return 'Outcrops/Analogues';
  } else if (hasOutcropType) {
    return 'Outcrops';
  } else {
    return 'Analogues';
  }
}

export default function StudyIndexPage() {
  return (
    <ApolloProviderV4>
      <StudyIndexPageInner />
    </ApolloProviderV4>
  );
}

function StudyIndexPageInner() {
  const studyId = useRouteParam('studyId', parseInt);

  const { data, loading } = useQuery(STUDY_INDEX_PAGE, {
    variables: { id: studyId },
  });

  const study = data?.studyGet;
  const outcrops = study?.outcrops.slice().sort(R.ascend(oc => oc.name)) ?? [];
  const hasOutcrops = outcrops.length > 0;

  const hasLiterature =
    study?.literatureReferences && study.literatureReferences.length > 0;

  const showColumns = hasOutcrops || hasLiterature;

  if (loading) return <SpinnerPlaceholder />;
  if (!study) return <NotFound />;

  return (
    <>
      <div className="grid lg:grid-cols-12 gap-4">
        <div
          className={cn(
            'space-y-4',
            showColumns ? 'lg:col-span-8' : 'lg:col-span-12',
          )}
        >
          {study.abstract && (
            <HtmlSection
              title="Abstract"
              content={study.abstract}
              truncate
              truncateLength={1500}
            />
          )}

          {study.numericalDataAspects && (
            <div>
              <Heading level={3}>
                Numerical Data and Other Notable Aspects
              </Heading>
              <Prose
                dangerouslySetInnerHTML={{ __html: study.numericalDataAspects }}
              />
            </div>
          )}

          {study.keyReferences && (
            <div>
              <Heading level={3}>Citation</Heading>
              <Prose
                dangerouslySetInnerHTML={{ __html: study.keyReferences }}
              />
            </div>
          )}

          <StudyGeoreferencesMap study={study} />
        </div>

        {showColumns && (
          <div className="lg:col-span-4 space-y-4">
            {hasOutcrops && (
              <div>
                <Heading level={3}>
                  {outcropCategoryLabel(study.outcrops)}
                </Heading>
                <StudyOutcropList outcrops={outcrops} />
              </div>
            )}

            {study.keywords && (
              <div>
                <Heading level={3}>Keywords</Heading>
                <p>{study.keywords.split(',').join(', ')}</p>
              </div>
            )}

            {hasLiterature && (
              <div>
                <Heading level={3}>Literature</Heading>
                <div className="space-y-4">
                  {study.literatureReferences.map(lit => (
                    <StudyLiteraturePanel key={lit.id} literature={lit} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <LastUpdated date={study.updatedAt} />
    </>
  );
}
